import { FC } from 'react';
import { AuthProviderWithHistoryComponent } from '@src/core/auth/components/auth-provider-with-history.component';
import { ConsentGateProvider } from '@src/core/consent/consent-gate.provider';
import { Client } from '@stomp/stompjs';
import NiceModal from '@ebay/nice-modal-react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { LocalizationProvider } from '@progress/kendo-react-intl';
import {
  AssumedIdentityData,
  FlatComponent,
  FlatProvider,
  SessionProvider,
} from 'swo-ciam-common-react';

import {
  TokenProvider,
  TokenProviderContext,
} from '@src/core/auth/components/token-provider.component';
import { ServiceRegistryProvider } from '@src/server/service-registry.provider';
import { DynamicHeaderProvider } from '@src/core/dynamic-header-context/dynamic-header-context';
import { MonitoringProvider } from '@src/core/monitoring/monitoring-provider.component';

import { commonHttpClientConfig } from '@src/server/api-config';
import { ServiceModuleProvider } from '@src/domain/context/service-module-context';
import { useHttpClient } from '@src/server/cmp-http-client-hook';
import {
  AssumeSessionContext,
  AssumeSessionProvider,
} from '@src/core/auth/assume-session/assume-session-provider.component';
import { AssumeSessionContextInterface } from '@src/core/auth/assume-session/assume-session-context.interface';
import { ApplicationConfigurationService } from './application-configuration';
import { UserPolicyProvider } from './terms-and-conditions/user-policy-provider.component';
import { TokenProviderContextInterface } from './auth/components/types/token-provider-context.interface';
import { RBACProtectionProvider } from './rbac/rbac-protection.provider';
import FlagContextUpdateProvider from './flags/context-update.provider';
import { LocaleProvider } from './i18n/locale.provider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export const PreloadComponent: FC = () => {
  const { i18n } = useTranslation();

  const { cmpHttpClient, updateClientConfiguration } = useHttpClient(
    ApplicationConfigurationService.getConfiguration().VITE_API_URL,
    commonHttpClientConfig
  );

  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const apiUrl = ApplicationConfigurationService.getConfiguration().CIAM_GATEWAY_URL_DEV;
  const socketUrl = `${apiUrl.replace(/^https?/, protocol)}/ws`;

  const bootstrapProviders: FlatComponent<void>[] = [
    <ConsentGateProvider />,
    <MonitoringProvider />,
    <DynamicHeaderProvider />,
    <LocalizationProvider language={i18n.language} />,
    <ServiceRegistryProvider cmpHttpClient={cmpHttpClient} />,
    <NiceModal.Provider />,
    <QueryClientProvider client={queryClient} />,
    <AuthProviderWithHistoryComponent />,
    <TokenProvider updateHttpClient={updateClientConfiguration} />,
    <FlagContextUpdateProvider />,
    <AssumeSessionProvider />,
    <RBACProtectionProvider />,
    <LocaleProvider />,
  ];

  const applicationProviders: FlatComponent<unknown>[] = [
    <ServiceModuleProvider />,
    {
      element: UserPolicyProvider,
      enabledWhen: ({ isFullyAuthenticated }: TokenProviderContextInterface) =>
        isFullyAuthenticated,
      context: TokenProviderContext,
    },
    // Position for future Qualtrics (new Wootric) integration
    {
      element: SessionProvider as never,
      enabledWhen: (context: AssumeSessionContextInterface) => !!context.assumedIdentityData,
      context: AssumeSessionContext,
      parameters: {
        socketClient: new Client({
          reconnectDelay: 5000,
          heartbeatIncoming: 4000,
          heartbeatOutgoing: 4000,
          brokerURL: socketUrl,
        }),
        brokerURL: socketUrl,
        getExpireURL: (assumedIdentityData: AssumedIdentityData) =>
          `/user/${assumedIdentityData.assumerId}/identity-expired`,
        context: AssumeSessionContext,
      },
    },
  ];

  return (
    <FlatProvider elements={[...bootstrapProviders, ...applicationProviders]}>
      <Outlet />
    </FlatProvider>
  );
};
